import type { ApolloError, OperationVariables } from '@apollo/client';
import type { DocumentNode, GraphQLError } from 'graphql';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Subscription } from 'rxjs';

import { CIError } from '../error/error_util';
import { gqlSimpleQuery } from '../graphql/utils';

import { usePageStatus } from './use-page-status';

export interface QueryState<EntityType extends object> {
    data: EntityType | undefined;
    isLoading: boolean;
    error?: GraphQLError | ApolloError;
}

/*
    Maintained by David Mlnarik. Please add me to review when you modify this file.
 */
export function useQuery<EntityType extends object, ResponseType extends object = EntityType>(
    query: string | DocumentNode,
    {
        queryString,
        variables,
        hideError,
        skip,
        responseMapper = (data) => data as object as EntityType,
        generateFields,
        headers = {},
    }: {
        variables?: OperationVariables;
        queryString?: string;
        hideError?: boolean;
        skip?: boolean;
        generateFields?: string[];
        headers?: Record<string, string>;
        responseMapper?: (response: ResponseType) => EntityType;
    } = { responseMapper: (data) => data as object as EntityType }
) {
    const pageStatus = usePageStatus();
    const subscription = useRef<Subscription>();
    const { t } = useTranslation();
    const [{ data, isLoading, error }, setState] = useState<QueryState<EntityType>>({
        data: undefined,
        isLoading: true,
        error: undefined,
    });

    function fetch(values?: Partial<OperationVariables>) {
        setState((state) => (!state.isLoading ? { ...state, isLoading: true } : state));

        subscription.current?.unsubscribe();
        subscription.current = gqlSimpleQuery<ResponseType>(query, {
            queryString,
            variables: !isEmpty(variables) || !isEmpty(values) ? { ...variables, ...values } : undefined,
            generateFields,
            headers,
        }).subscribe({
            next: (data) => {
                setState({
                    data: responseMapper(data),
                    isLoading: false,
                    error: undefined,
                });
            },
            error: (error) => {
                if (!hideError) {
                    pageStatus.error(t(CIError.fromGraphQLError(error)?.translationKey));
                }
                setState({
                    data: undefined,
                    isLoading: false,
                    error,
                });
            },
        });
    }

    useEffect(() => {
        if (!skip) {
            fetch();
        } else {
            setState({
                data: undefined,
                isLoading: false,
                error: undefined,
            });
        }

        return () => {
            subscription.current?.unsubscribe();
        };
    }, [skip]);

    return { data, isLoading, error, refetch: fetch };
}
